import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import {
  getAuftragAppAngebot,
  type AppAngebotBundle,
} from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import { formatHours } from "@/pages/projekte/hoursUsage";
import { HoursUsageOverview } from "@/projectOverview/HoursUsageOverview";
import { KalkStundenOverview } from "@/projectOverview/KalkStundenOverview";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import { useProjectHoursUsage } from "@/projectOverview/useProjectHoursUsage";

function resolveOfferList(bundle: AppAngebotBundle | null): AppAngebotBundle[] {
  if (!bundle) return [];
  if (Array.isArray(bundle.angebote) && bundle.angebote.length > 0) {
    return bundle.angebote.filter((item) => item?.dokument_id != null);
  }
  return bundle.dokument_id != null ? [bundle] : [];
}

/** App-Angebote + Stundenverbrauch in der Projektübersicht. */
export function OfferPositionsSection({ project }: { project: Order }) {
  const orderId = project.id;
  const [bundle, setBundle] = useState<AppAngebotBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { entry: hoursEntry, workshopHours, montageHours } = useProjectHoursUsage(project);
  const showHours = Boolean(hoursEntry?.eligible);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getAuftragAppAngebot(orderId)
      .then((data) => {
        if (!cancelled) setBundle(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setBundle(null);
          setError(err instanceof Error ? err.message : "Angebot konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const offers = useMemo(() => resolveOfferList(bundle), [bundle]);
  const kalkStunden = Array.isArray(bundle?.kalk_stunden) ? bundle.kalk_stunden : [];
  const nachweisStunden =
    bundle?.nachweis_stunden != null && Number(bundle.nachweis_stunden) > 0
      ? Number(bundle.nachweis_stunden)
      : null;
  const offerCount = offers.length;

  if (loading && !showHours) {
    return (
      <OverviewCard title="Angebote">
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      </OverviewCard>
    );
  }

  if (error && !offerCount && !showHours) {
    return (
      <OverviewCard title="Angebote">
        <Alert variant="error">{error}</Alert>
      </OverviewCard>
    );
  }

  if (!offerCount && !showHours) {
    return null;
  }

  if (!offerCount && showHours && hoursEntry) {
    return (
      <OverviewCard title="Stundenverbrauch">
        <HoursUsageOverview
          entry={hoursEntry}
          workshopHours={workshopHours}
          montageHours={montageHours}
        />
      </OverviewCard>
    );
  }

  const firstOffer = offers[0];
  const summaryLabel =
    offerCount === 1
      ? firstOffer?.dokument_nummer || "1 Angebot"
      : `${offerCount} beauftragte Angebote`;

  return (
    <OverviewCard title="Angebote">
      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : error ? (
          <Alert variant="error">{error}</Alert>
        ) : (
          <>
            <p className="text-sm text-label-secondary">
              {summaryLabel}
              {offerCount === 1 && firstOffer?.betreff ? ` · ${firstOffer.betreff}` : null}
            </p>

            {kalkStunden.length > 0 ? (
              <KalkStundenOverview
                items={kalkStunden}
                title={
                  offerCount > 1
                    ? "Kalkulierte Festpreis-Stunden (alle Angebote)"
                    : "Kalkulierte Festpreis-Stunden"
                }
              />
            ) : (
              <p className="text-sm text-label-secondary">Keine Festpreis-Stunden kalkuliert.</p>
            )}

            {nachweisStunden != null ? (
              <div className="flex justify-between gap-3 text-sm text-label">
                <span className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                  Angebotene Nachweisstunden
                  {offerCount > 1 ? " (alle Angebote)" : null}
                </span>
                <span className="tabular-nums font-medium">{formatHours(nachweisStunden)}</span>
              </div>
            ) : null}
          </>
        )}

        {showHours && hoursEntry ? (
          <div className="border-t border-separator pt-3">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
              Stundenverbrauch
            </h4>
            <HoursUsageOverview
              entry={hoursEntry}
              workshopHours={workshopHours}
              montageHours={montageHours}
            />
          </div>
        ) : null}
      </div>
    </OverviewCard>
  );
}
